/**
 * The collider half of `HallSolid`, built from the same rotation convention
 * as its mesh.
 *
 * This used to be a closure inside `main.ts` that built its own
 * `THREE.Euler(pitch, yaw, 0)` — default `'XYZ'` order — while
 * `src/world/solids.ts` baked the mesh with `rotateX` then `rotateY`
 * (`Ry * Rx`). The two agree only when at most one of pitch/yaw is nonzero;
 * F1 measured a 0.2223 m corner mismatch once both are set
 * (`docs/engine-architecture-audit.md`). `solidQuaternion` is now the one
 * place that decides the order, and this function is its only collider
 * consumer.
 */

import type { PhysicsWorld } from '@/physics/world';
import { solidQuaternion } from '@/world/solids';

export interface SolidColliderOptions {
  readonly half: { x: number; y: number; z: number };
  readonly pos: { x: number; y: number; z: number };
  // `| undefined` rather than a bare optional: callers like `main.ts`'s
  // `boxCollider` forward their own optional parameters straight through, and
  // `exactOptionalPropertyTypes` rejects `{ pitch }` where `pitch` is typed
  // `number | undefined` against a plain `pitch?: number` target.
  readonly pitch?: number | undefined;
  readonly yaw?: number | undefined;
}

export function solidColliderDesc(
  rapier: PhysicsWorld['rapier'],
  options: SolidColliderOptions,
): ReturnType<PhysicsWorld['rapier']['ColliderDesc']['cuboid']> {
  const { half, pos, pitch, yaw } = options;
  let desc = rapier.ColliderDesc.cuboid(half.x, half.y, half.z).setTranslation(pos.x, pos.y, pos.z);
  if (pitch !== undefined || yaw !== undefined) {
    const q = solidQuaternion({ pitch, yaw });
    desc = desc.setRotation({ x: q.x, y: q.y, z: q.z, w: q.w });
  }
  return desc;
}
